import React, {useEffect, useState} from 'react';
import {Transition} from "@headlessui/react";
import AccountField from "../ui/account/AccountField";
import CurrencyPicker from "../ui/account/CurrencyPicker";
import {categoryApi} from "../../api/CategoriesService";
import {accountApi} from "../../api/AccountsService";
import {IAccount, IAccountReq} from "../../model/IAccount";
import AmountField from "../ui/transaction/AmountField";
import AccountLogoPicker from "../ui/account/AccountLogoPicker";

interface NewAccountFormProps {
    show: boolean;
    onClose?: () => void;
    onCreated?: (account: IAccount) => void;
}

const NewAccountForm: React.FunctionComponent<NewAccountFormProps> = ({show, onClose, onCreated}) => {
    const {data: currencies} = categoryApi.useFetchAllCurrenciesQuery();
    const {data: accountLogos} = categoryApi.useFetchAllAccountLogosQuery();

    const [accountName, setAccountName] = useState('');
    const [accountLogo, setAccountLogo] = useState(null as any);
    const [currency, setCurrency] = useState(null as any);
    const [initialAmount, setInitialAmount] = useState(0.0);

    function clearForm() {
        setAccountName('');
        setAccountLogo(accountLogos ? accountLogos[0] : null);
        setCurrency(currencies ? currencies[0] : null);
        setInitialAmount(0);
    }

    const [addNewAccount, {isLoading}] = accountApi.useCreateAccountMutation();

    function composeNewAccount() : IAccountReq {
        return {
            name: accountName,
            currency: currency.id,
            logo: accountLogo,
            initial_balance: initialAmount,
        }
    }

    async function handleAddNewAccount() {
        const result = await addNewAccount(composeNewAccount());
        if ('data' in result) {
            clearForm();
            onClose?.();
            onCreated?.(result.data);
        } else {
            console.log(result.error);
        }
    }

    useEffect(() => {
        if (!currency && currencies) {
            setCurrency(currencies[0]);
        }
    }, [currencies]);

    useEffect(() => {
        if (!accountLogo && accountLogos) {
            setAccountLogo(accountLogos[0]);
        }
    }, [accountLogos]);

    return (
        <Transition
            show={show}
            enter="ease-in-out duration-300 transform"
            enterFrom="-translate-y-full opacity-0"
            enterTo="translate-y-0 opacity-100"
            leave="ease-in-out duration-300 transform"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="-translate-y-full opacity-0"
            className="relative z-50"
        >
            <div id="account" className="mb-2 border-b border-gray-200 relative z-50 py-1">
                <div className="relative z-50 flex items-end justify-center flex-wrap mx-3 px-1 focus-within:z-[60]">
                    {accountLogo && accountLogos && (
                        <AccountLogoPicker logo={accountLogo} setLogo={setAccountLogo} logos={accountLogos} smallerSize/>
                    )}
                    <AccountField label={'Account Name'} accountName={accountName} setAccountName={setAccountName} smallerSize/>
                    {currency && (
                        <CurrencyPicker label={'Currency'} currency={currency} setCurrency={setCurrency} currencies={currencies} smallerSize/>
                    )}
                    {currency && (
                        <AmountField
                            label={'Initial Balance'}
                            currency={currency}
                            amount={initialAmount}
                            setAmount={setInitialAmount}
                            smallerSize
                            className="mt-2 mx-3"
                        />
                    )}
                    <div className="flex items-center ml-3 mb-0.5 gap-2">
                        <button
                            className="group relative flex justify-center py-1 px-3 border border-transparent
                                       text-xs font-medium rounded-md bg-gray-200 text-red-500 hover:text-red-800"
                            onClick={() => {
                                clearForm();
                                onClose?.();
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            className="group relative flex justify-center py-1 px-3 border border-transparent
                                       text-xs font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none"
                            disabled={isLoading}
                            onClick={handleAddNewAccount}
                        >
                            Add
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    );
};

export default NewAccountForm;
